import { useMemo } from 'react'
import { z, ZodObject } from 'zod'
import { FormFieldType } from 'src/enums'
import { FormRow, getFieldKey, IChecklistItemValue } from 'src/generated-types/form-field'
import { ICustomerFormSchema } from 'src/generated-types/form-schema'

export const schemaHasRequiredFields = (schema: ICustomerFormSchema): boolean => {
  try {
    const layouts: FormRow[] = JSON.parse(schema.version?.layout ?? '[]')
    return layouts.some(row =>
      (row.columns ?? []).some(col =>
        (col?.fields ?? []).some(field => field.type !== FormFieldType.title && field.isRequired),
      ),
    )
  } catch {
    return false
  }
}

const isEmptyChecklistItem = (item: IChecklistItemValue): boolean =>
  item?.status == null && !item?.comment?.trim() && !item?.deviation

const isEmptyFieldValue = (value: any): boolean => {
  if (value === null || value === undefined) return true
  const str = typeof value === 'string' ? value.trim() : JSON.stringify(value)
  if (str === '' || str === '[]') return true
  try {
    const parsed = JSON.parse(str)
    if (Array.isArray(parsed)) return parsed.every(isEmptyChecklistItem)
  } catch {
    return false
  }

  return false
}

export const isEmptyReportValues = (values: Record<string, any>): boolean =>
  Object.values(values).every(isEmptyFieldValue)

export const parseLayout = (layout?: string | FormRow[]): FormRow[] => {
  if (!layout) return []
  if (typeof layout !== 'string') return layout
  try {
    return JSON.parse(layout)
  } catch {
    return []
  }
}

export const buildSchemaAndInitialValues = (layout?: string | FormRow[], savedValues?: Record<string, any>) => {
  let zodSchema: ZodObject<any> = z.object({})
  const initialValues: Record<string, any> = {}
  const layouts = parseLayout(layout)

  layouts.forEach(row => {
    row.columns?.forEach(col => {
      col?.fields.forEach(field => {
        if (field.type === FormFieldType.title) return

        const key = getFieldKey(field)
        initialValues[key] = savedValues?.[key] ?? savedValues?.[field.name] ?? ''

        if (!field.isRequired) {
          zodSchema = zodSchema.merge(z.object({ [key]: z.string().optional() }))
          return
        }

        if (field.type === FormFieldType.checklist && field.checklistItems?.length) {
          zodSchema = zodSchema.merge(
            z.object({
              [key]: z.string().refine(val => {
                try {
                  const filled: IChecklistItemValue[] = val ? JSON.parse(val) : []
                  return field.checklistItems!.every(item => filled.find(v => v.id === item.id)?.status != null)
                } catch {
                  return false
                }
              }),
            }),
          )
          return
        }

        zodSchema = zodSchema.merge(z.object({ [key]: z.string().min(1) }))
      })
    })
  })

  return { zodSchema, initialValues, layouts }
}

export const parseReportValues = (values?: string): Record<string, any> => {
  try {
    return values ? JSON.parse(values) : {}
  } catch {
    return {}
  }
}

export const useChecklistValuesForm = (
  values: Record<string, any>,
  onFieldChange: (fieldKey: string, value: any) => void,
) =>
  useMemo(
    () => ({
      watch: () => values,
      setValue: (fieldKey: string, value: any) => onFieldChange(fieldKey, value),
      getValues: () => values,
      formState: { errors: {} },
    }),
    [values, onFieldChange],
  )
